import { FC, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnyTask, Point, TaskRoute } from "tm-shared/types";
import { isTaskStartVisit } from "tm-shared/utils";

import { ROUTES } from "@/constants";
import { useAppSelector, useOfflineModeSelector } from "@/hooks/redux";
import { isTaskRoute } from "@/utils";

import { PointTasksModalState, PointTasksModalType } from "./PointTasks.types";
import { PointTasksModalsContainer } from "./PointTasksModalsContainer";
import { TaskRow } from "./TaskRow";

const OFFLINE_ENABLED_ROUTES: TaskRoute[] = [
    TaskRoute.StartVisit,
    TaskRoute.SaleAdditional,
    TaskRoute.RejectProducts,
    TaskRoute.TakeDebt,
    TaskRoute.ShelfSize,
    TaskRoute.SetGeoLocation,
    TaskRoute.UpdateProductBalances,
    TaskRoute.AssortmentMatrix,
    TaskRoute.Dmp,
    TaskRoute.PromoTagPhotos,
    TaskRoute.AddPhoto,
    TaskRoute.UpdateMarketInfo,
    TaskRoute.CustomTask,
    TaskRoute.PaidShelfSpace,
];

const offlineEnabledRoutesSet = new Set<TaskRoute>(OFFLINE_ENABLED_ROUTES);

const BLOCKERS: Array<{ blocker: TaskRoute; allowed: Set<TaskRoute> }> = [
    {
        blocker: TaskRoute.StartVisit,
        allowed: new Set([TaskRoute.StartVisit]),
    },
    {
        blocker: TaskRoute.SetGeoLocation,
        allowed: new Set([TaskRoute.SetGeoLocation, TaskRoute.StartVisit]),
    },
];

interface PointTasksProps {
    tasks: AnyTask[];
    point: Point;
    onOpenTasksInfoModal: (params: { task: AnyTask; point: Point }) => void;
    incompleteRoutes: Set<TaskRoute>;
    readonly?: boolean;
}

export const PointTasks: FC<PointTasksProps> = ({
    tasks,
    point,
    readonly = false,
    incompleteRoutes,
    onOpenTasksInfoModal,
}) => {
    const navigate = useNavigate();
    const requestQueue = useAppSelector((s) => s.offline.requestQueue);
    const offlineMode = useOfflineModeSelector();

    const [modal, setModal] = useState<PointTasksModalState>(null);

    const isTaskEnabled = useCallback(
        (task: AnyTask): boolean => {
            const route = task.context.route;

            const gate = BLOCKERS.find((g) => incompleteRoutes.has(g.blocker));
            if (gate && !gate.allowed.has(route)) return false;

            if (offlineMode && !offlineEnabledRoutesSet.has(route))
                return false;

            return true;
        },
        [incompleteRoutes, offlineMode],
    );

    const handleTaskClick = useCallback(
        (task: AnyTask) => {
            if (readonly || !isTaskEnabled(task)) return;

            const queueItem = requestQueue.find((item) => item.id === task.id);

            // якщо завершена і в черзі на синхронізацію
            if (task.completed && queueItem) {
                setModal({
                    type: PointTasksModalType.TaskProgress,
                    payload: {
                        requestQueueItem: queueItem,
                    },
                });

                return;
            }

            // якщо онлайн і дані в черзі на синхронізацію
            if (queueItem && !offlineMode) {
                setModal({
                    type: PointTasksModalType.TaskProgress,
                    payload: {
                        requestQueueItem: queueItem,
                    },
                });

                return;
            }

            // якщо завершена і не в черзі
            if (task.completed) {
                onOpenTasksInfoModal({ task, point });
                return;
            }

            if (isTaskStartVisit(task)) {
                setModal({
                    type: PointTasksModalType.StartVisit,
                    payload: {
                        point,
                        taskId: task.id,
                    },
                });

                return;
            }

            const route = task.context.route;

            // if (route in TaskRoute) {
            if (isTaskRoute(route)) {
                navigate(
                    `${ROUTES.WorkPlan}/${task.targetId}/${task.context.route}/${task.id}`,
                );

                return;
            }

            console.error("No handler for route:", route, task);
        },
        [
            readonly,
            isTaskEnabled,
            requestQueue,
            offlineMode,
            onOpenTasksInfoModal,
            point,
            navigate,
        ],
    );

    return (
        <>
            {tasks.map((task) => {
                const queueItem = requestQueue.find(
                    (item) => item.id === task.id,
                );

                const status = queueItem?.status ?? null;

                return (
                    <TaskRow
                        key={task.id}
                        task={task}
                        onTaskClick={handleTaskClick}
                        isTaskEnabled={isTaskEnabled}
                        status={status}
                    />
                );
            })}

            <PointTasksModalsContainer modal={modal} onChangeModal={setModal} />
        </>
    );
};
